import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import useBodyScrollLock from '../../utils/useBodyScrollLock';
import './BeneficiariesModal.css';

const BeneficiariesModal = ({ isOpen, onClose, initialTags = [], onSave }) => {
  const [rows, setRows] = useState([]);
  const [error, setError] = useState('');

  useBodyScrollLock(isOpen);

  useEffect(() => {
    // convert tag strings to row objects when modal opens or initialTags change
    if (isOpen) {
      const parsed = initialTags
        .map(t => t.split('=').map(s => s.trim()))
        .filter(p => p.length === 2)
        .map(([acct, w]) => {
          let num = parseInt(w, 10) || 0;
          // if we stored hundredths previously, convert down to percent
          if (num > 100) num = Math.round(num / 100);
          return { account: acct, weight: num };
        });
      setRows(parsed.length ? parsed : [{ account: '', weight: 0 }]);
    }
  }, [isOpen, initialTags]);

  const updateRow = (index, newRow) => {
    const updated = [...rows];
    updated[index] = newRow;
    setRows(updated);
  };

  // handle weight input string changes by trimming any leading zeros
  const handleWeightInput = (index, value) => {
    let str = value || '';
    // strip leading zeros entirely ("05" -> "5"), but keep "0" alone
    str = str.replace(/^0+/, '');
    if (str === '') str = '0';
    const num = parseInt(str, 10) || 0;
    updateRow(index, { ...rows[index], weight: num });
  };

  const addRow = () => {
    setRows([...rows, { account: '', weight: 0 }]);
  };

  const removeRow = (index) => {
    const updated = rows.filter((_, i) => i !== index);
    setRows(updated.length ? updated : [{ account: '', weight: 0 }]);
  };

  const changeWeight = (index, delta) => {
    const r = rows[index];
    const neww = Math.max(0, Math.min(100, r.weight + delta));
    updateRow(index, { ...r, weight: neww });
  };

  const handleSave = () => {
    const total = rows.reduce((sum, r) => sum + (r.weight || 0), 0);
    if (total > 100) {
      setError('Total allocation cannot exceed 100%');
      return;
    }
    const tags = rows
      .filter(r => r.account && r.weight > 0)
      .map(r => `${r.account}=${r.weight}`);
    onSave(tags);
    onClose();
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="modal-backdrop" onClick={e => e.target.className === 'modal-backdrop' && onClose()}>
      <div className="beneficiaries-modal">
        <div className="modal-header">
          <h2>Beneficiaries</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        <div className="beneficiaries-modal-body">
          <p className="mb-10">Add a user you want to automatically receive a portion of the rewards for this post.</p>
          <table className="beneficiaries-table">
            <thead>
              <tr>
                <th className="username">Username</th>
                <th className="reward text-center" style={{ minWidth: 120 }}>Reward %</th>
                <th className="controls text-center"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={i}>
                  <td>
                    <input
                      type="text"
                      className="form-control tag-input"
                      placeholder="Account"
                      value={row.account}
                      onChange={e => updateRow(i, { ...row, account: e.target.value })}
                    />
                  </td>
                  <td className="text-center">
                    <div className="weight-input">
                      <button type="button" className="btn btn-default btn-beneficiary-increment" onClick={() => changeWeight(i, -1)}>-</button>
                      <input
                        type="number"
                        className="form-control text-semibold tag-input"
                        style={{ maxWidth: 55 }}
                        min="0"
                        max="100"
                        value={row.weight}
                        onChange={e => handleWeightInput(i, e.target.value)}
                      />
                      <button type="button" className="btn btn-default btn-beneficiary-increment" onClick={() => changeWeight(i, 1)}>+</button>
                    </div>
                  </td>
                  <td className="text-center">
                    <button type="button" className="btn btn-danger btn-beneficiary-xs" onClick={() => removeRow(i)} aria-label="Remove">×</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="mt-10">
            <button type="button" className="secondary-btn blwh-btn" onClick={addRow}>Add beneficiary <svg class="btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg></button>
          </div>
          {error && <div className="error-message" style={{marginTop:8}}>{error}</div>}
        </div>
        <div className="modal-actions text-right">
          <button type="button" className="cancel-form-btn" onClick={onClose}>Cancel</button>
          <button
            type="button"
            className="submit-form-btn"
            onClick={handleSave}
            disabled={rows.reduce((sum, r) => sum + (r.weight || 0), 0) > 100}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );

  if (typeof document === 'undefined') {
    return modalContent;
  }

  return createPortal(modalContent, document.body);
};

export default BeneficiariesModal;
